import * as React from "react";
import { cn } from "../../lib/cn";
import { Markdown } from "../Markdown";
import { Chart, type ChartSpec } from "./Chart";
import { isFlatChartProps } from "./chartSpec";
import { DataTable, type CellValue, type DataTableColumnInput } from "./DataTable";
import { Stat } from "./Stat";
import { Citation } from "./Citation";
import { CodeBlock } from "./CodeBlock";
import { ArtifactBlock, type ArtifactStatus } from "./ArtifactBlock";
import { Callout } from "./Callout";
import { Media, type MediaKind } from "./Media";
import { KeyValue, type KeyValuePair } from "./KeyValue";
import { Carousel, type CarouselEntry } from "./Carousel";
import { Reasoning, type ReasoningStep } from "./Reasoning";
import { Steps, type StepItem } from "./Steps";
import { Suggestions, type SuggestionChip } from "./Suggestions";
import { Form, type FormField } from "./Form";
import { Approval, type ApprovalOption } from "./Approval";
import { MemorySave } from "./MemorySave";
import { EnrichedSuggestion, type EnrichedSuggestionItem } from "./EnrichedSuggestion";
import { ToolGateCorrection } from "./ToolGateCorrection";
import { BetterAnswer } from "./BetterAnswer";
import { BlockSubmitProvider, useBlockSubmit, type BlockSubmitHandler } from "./blockSubmit";
import { BlockRegistryProvider, type BlockRefResolver } from "./blockChildren";
import { BLOCK_STACK_GAP } from "./blockSurface";

/**
 * BlockRenderer - renders one K2 block envelope to the right widget.
 *
 * The platform streams rich chat UI as `data-block` parts, each a typed,
 * schema-validated envelope (see ais-agent-runtime
 * `docs/contracts/block-registry.md` + `stream-protocol.md`). This component is
 * the single render seam both Creator Studio (FE-React) and the Studio template
 * (AIStudio-v3) consume so a block renders identically on both surfaces.
 *
 * Envelope (K2):
 *
 *   { block_id, type, version, props, layout? }
 *
 * Type -> renderer mapping (`registry`):
 *   - `chart`    -> `Chart`     (the flat BE#2577 props - `chart_type` /
 *     `x` / `series` / `data` / `value_format` / `summary` / ... - or the
 *     legacy nested `props.spec`; recharts)
 *   - `table`    -> `DataTable` (props.columns / rows / sort? / page?)
 *   - `stat`     -> `Stat`      (props.label / value / delta? / unit?)
 *   - `citation` -> `Citation`  (props.title / url / snippet?)
 *   - `code`     -> `CodeBlock` (props.language / source)
 *   - `artifact` -> `ArtifactBlock` (props.name / mime / size_bytes /
 *     download_url / status) - a downloadable file card, download live at any
 *     status
 *   - `callout`  -> `Callout`   (props.variant / title? / body)
 *   - `media`    -> `Media`     (props.kind / src / alt? / caption? / ...)
 *   - `key_value`-> `KeyValue`  (props.title? / pairs / columns?)
 *   - `carousel` -> `Carousel`  (props.title? / items; composition + actions)
 *   - `reasoning`-> `Reasoning` (props.title? / steps? / text? / defaultOpen?)
 *   - `steps`    -> `Steps`     (props.title? / steps / elapsedMs? / receipt?)
 *   - `suggestions` -> `Suggestions` (props.suggestions / submit_to; INTERACTIVE)
 *   - `form`     -> `Form`      (props.fields / submit_to / receipt?; INTERACTIVE)
 *   - `approval` -> `Approval`  (props.options / submit_to / receipt?; INTERACTIVE)
 *   - Post-process signals (ais-agent-runtime#115 PostProcessSignals):
 *     - `memory_save` -> `MemorySave` (props.text / submit_to? / receipt?;
 *       INTERACTIVE) - the "save this to memory?" affordance
 *     - `enriched_suggestion` -> `EnrichedSuggestion` (props.label/kind?/
 *       confidence? or a suggestions list; INTERACTIVE) - metadata-rich chips
 *     - `tool_gate_correction` -> `ToolGateCorrection` (props.message; DISPLAY) -
 *       inline "use the control" footnote from the `missing_tool_use` signal
 *     - `better_answer` -> `BetterAnswer` (props.kind/label/rewrittenQuery;
 *       INTERACTIVE) - the shared "I can answer this better" nudge
 *   - `markdown` / `text` -> `Markdown`
 *   - `html`     -> a sandboxed iframe (`sandbox="allow-scripts"`, never
 *     `allow-same-origin`, plus a restrictive CSP) - the only path for
 *     arbitrary HTML, isolated from the host page.
 *   - unknown / malformed -> a safe placeholder that never throws.
 *
 * Interactive blocks (`suggestions`, `form`, `approval`, `memory_save`,
 * `enriched_suggestion`, `better_answer`, an actioned `carousel`) emit ONE
 * shared submit event keyed by `block_id`; wire `onBlockSubmit` to receive it.
 * They also adopt the `receipt` settled-state
 * convention: an optional `props.receipt` ({outcome, summary, identifiers?,
 * at}) re-renders the block in a settled "this is what happened" state instead
 * of a live control, so history reloads are replay-safe.
 *
 * Safety contract (block-registry.md): an unknown `type`, missing required
 * props, or a renderer that throws must degrade to a safe placeholder and never
 * crash the thread. Every block is wrapped in an error boundary so a single bad
 * block cannot take down the surrounding conversation.
 *
 * Extension: pass `registry` to override or add renderers without forking this
 * file. Your entries shallow-merge over the built-ins (`mergeRegistry`), so you
 * can add a `form` renderer or replace `chart` with a bespoke one and leave the
 * rest untouched.
 *
 *   <BlockRenderer block={block} registry={{ form: MyFormBlock }} />
 *
 * Theming is token-driven throughout (`bg-card`, `border-border`,
 * `text-muted-foreground`, ...) - no hardcoded colors. SSR-safe: no `window` /
 * `document` access at module top or during render (the iframe `srcDoc` and the
 * widgets it delegates to are all SSR-safe).
 */

/* ============================================================================
 * Block envelope + registry types
 * ========================================================================= */

/**
 * K2 block types v0 (block-registry.md). Open union: an unknown string is a
 * valid `type` at the type level and renders the safe placeholder at runtime,
 * so a runtime that ships a new block type never breaks a deployed frontend.
 */
export type BlockType =
  | "markdown"
  | "text"
  | "table"
  | "chart"
  | "stat"
  | "citation"
  | "code"
  | "artifact"
  | "form"
  | "approval"
  | "html"
  | "grid"
  // Tier-1 rich blocks (docs/rich-blocks-catalog.md).
  | "callout"
  | "media"
  | "key_value"
  | "carousel"
  | "reasoning"
  | "steps"
  | "suggestions"
  // Post-process signal blocks (ais-agent-runtime#115 PostProcessSignals).
  | "memory_save"
  | "enriched_suggestion"
  | "tool_gate_correction"
  | "better_answer"
  | (string & {});

/** Grid placement, present only for blocks inside a `grid` (block-registry.md). */
export interface BlockLayout {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * A K2 block envelope. `block_id` is stable across progressive updates so a
 * renderer can patch in place. `props` is intentionally `unknown` - each
 * renderer narrows + validates its own shape and falls back safely.
 */
export interface Block {
  /** Stable id across progressive updates (append rows, restyle a chart). */
  block_id: string;
  /** Discriminator selecting the renderer. */
  type: BlockType;
  /** Schema version; bumped on any breaking prop change. */
  version?: number;
  /** Type-specific payload. Validated per-renderer. */
  props?: unknown;
  /** Grid placement; present only inside a `grid`. */
  layout?: BlockLayout;
}

/** A renderer for one block type. Receives the whole envelope. */
export type BlockComponent = React.ComponentType<{ block: Block }>;

/** A type -> renderer map. Partial so consumers override only what they need. */
export type BlockRegistry = Partial<Record<BlockType, BlockComponent>>;

/* ============================================================================
 * Defensive prop readers
 *
 * `props` arrives as `unknown` (a streamed payload that may be malformed).
 * These read a single field without throwing, so a renderer can decide between
 * "render" and "safe placeholder" instead of crashing on a missing key.
 * ========================================================================= */

function asRecord(props: unknown): Record<string, unknown> | undefined {
  return props && typeof props === "object" ? (props as Record<string, unknown>) : undefined;
}

function readString(props: unknown, key: string): string | undefined {
  const rec = asRecord(props);
  if (!rec || !(key in rec)) return undefined;
  const v = rec[key];
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return undefined;
}

function readUnknown(props: unknown, key: string): unknown {
  const rec = asRecord(props);
  return rec ? rec[key] : undefined;
}

function readArray(props: unknown, key: string): unknown[] | undefined {
  const v = readUnknown(props, key);
  return Array.isArray(v) ? v : undefined;
}

function readBoolean(props: unknown, key: string): boolean | undefined {
  const rec = asRecord(props);
  if (!rec || !(key in rec)) return undefined;
  return typeof rec[key] === "boolean" ? (rec[key] as boolean) : undefined;
}

/* ============================================================================
 * Built-in renderers
 *
 * Each unwraps + validates its slice of `props` and delegates to the existing
 * widget. None duplicate widget logic; they are thin adapters from the K2 block
 * shape to the widget's props.
 * ========================================================================= */

function MarkdownBlock({ block }: { block: Block }) {
  const text = readString(block.props, "text");
  if (text == null) return <UnsupportedBlock block={block} reason="missing text" />;
  return <Markdown content={text} />;
}

function ChartBlock({ block }: { block: Block }) {
  // Legacy shape: props are `{ spec }` (a nested recharts-style spec).
  const spec = readUnknown(block.props, "spec");
  if (spec && typeof spec === "object") {
    return <Chart spec={spec as ChartSpec} />;
  }
  // Flat BE#2577 contract (chart_block.py / render_chart.py): props ARE the
  // chart payload (`chart_type`, `x.key`, `series`, `value_format`, ...).
  // `Chart` resolves + degrades malformed fields itself, so a flat-looking
  // block always renders (worst case: the muted empty state, never a crash).
  if (isFlatChartProps(block.props)) {
    return <Chart spec={block.props} />;
  }
  return <UnsupportedBlock block={block} reason="missing chart spec" />;
}

function TableBlock({ block }: { block: Block }) {
  const columns = readUnknown(block.props, "columns");
  const rows = readUnknown(block.props, "rows");
  if (!Array.isArray(columns) || !Array.isArray(rows)) {
    return <UnsupportedBlock block={block} reason="missing columns or rows" />;
  }
  const sort = readUnknown(block.props, "sort");
  const page = readUnknown(block.props, "page");
  return (
    <DataTable
      columns={columns as DataTableColumnInput[]}
      rows={rows as CellValue[][]}
      sort={sort as React.ComponentProps<typeof DataTable>["sort"]}
      page={page as React.ComponentProps<typeof DataTable>["page"]}
    />
  );
}

function StatBlock({ block }: { block: Block }) {
  const label = readString(block.props, "label");
  const rawValue = readUnknown(block.props, "value");
  // `value` may legitimately be 0 / "", so check presence, not truthiness.
  if (label == null || rawValue == null) {
    return <UnsupportedBlock block={block} reason="missing label or value" />;
  }
  const value =
    typeof rawValue === "number" || typeof rawValue === "string" ? rawValue : String(rawValue);
  const unit = readString(block.props, "unit");
  const delta = readUnknown(block.props, "delta");
  return (
    <Stat
      label={label}
      value={value}
      unit={unit}
      delta={delta as React.ComponentProps<typeof Stat>["delta"]}
    />
  );
}

function CitationBlock({ block }: { block: Block }) {
  const title = readString(block.props, "title");
  const url = readString(block.props, "url");
  // The Citation widget renders inert (not crash) on a bad url, so the only
  // hard requirement here is a title to show.
  if (title == null && url == null) {
    return <UnsupportedBlock block={block} reason="missing source" />;
  }
  const snippet = readString(block.props, "snippet");
  return <Citation title={title ?? url ?? "Source"} url={url ?? ""} snippet={snippet} />;
}

function CodeBlockRenderer({ block }: { block: Block }) {
  const source = readString(block.props, "source");
  if (source == null) return <UnsupportedBlock block={block} reason="missing source" />;
  const language = readString(block.props, "language");
  return <CodeBlock language={language} source={source} />;
}

/** Narrow a streamed status string to the artifact union, defaulting to ready. */
function readArtifactStatus(props: unknown): ArtifactStatus {
  const raw = readString(props, "status");
  return raw === "processing" || raw === "error" ? raw : "ready";
}

function ArtifactBlockRenderer({ block }: { block: Block }) {
  // `name` is the one hard requirement - without it there is nothing to label
  // the file. Everything else (mime, size, url, status) degrades gracefully:
  // a missing url just hides the Download action, a bad status reads as ready.
  const name = readString(block.props, "name");
  if (name == null) return <UnsupportedBlock block={block} reason="missing name" />;
  const artifactId = readString(block.props, "artifact_id") ?? block.block_id;
  const rawSize = readUnknown(block.props, "size_bytes");
  return (
    <ArtifactBlock
      artifact_id={artifactId}
      name={name}
      mime={readString(block.props, "mime")}
      size_bytes={typeof rawSize === "number" ? rawSize : undefined}
      download_url={readString(block.props, "download_url")}
      status={readArtifactStatus(block.props)}
      run_id={readString(block.props, "run_id")}
      message_id={readString(block.props, "message_id")}
    />
  );
}

/* ============================================================================
 * html block - sandboxed iframe
 * ========================================================================= */

/**
 * The Content-Security-Policy injected into the sandboxed document. Blocks all
 * network egress (`default-src 'none'`) and external script / style loads,
 * while allowing the inline markup + styles the LLM authored to render. With
 * `sandbox="allow-scripts"` and NO `allow-same-origin`, scripts run in an
 * opaque origin: they cannot read the host's cookies, storage, or DOM, and the
 * CSP keeps them from phoning home.
 */
const HTML_BLOCK_CSP =
  "default-src 'none'; " +
  "style-src 'unsafe-inline'; " +
  "img-src data: blob:; " +
  "font-src data:; " +
  "script-src 'unsafe-inline'; " +
  "form-action 'none'; " +
  "base-uri 'none'";

/** Wrap the LLM-authored body in a full document carrying the CSP meta tag. */
function wrapHtmlDocument(srcdoc: string): string {
  return (
    '<!doctype html><html><head><meta charset="utf-8">' +
    `<meta http-equiv="Content-Security-Policy" content="${HTML_BLOCK_CSP}">` +
    '<meta name="color-scheme" content="light dark">' +
    "</head><body>" +
    srcdoc +
    "</body></html>"
  );
}

/**
 * `html` block renderer. Renders arbitrary HTML in a sandboxed iframe - the
 * only path for arbitrary markup (block-registry.md). The runtime policy gate
 * is expected to have cleared this content before streaming; the sandbox + CSP
 * are defense in depth on the client.
 */
function HtmlBlock({ block }: { block: Block }) {
  const srcdoc = readString(block.props, "srcdoc");
  if (srcdoc == null) return <UnsupportedBlock block={block} reason="missing srcdoc" />;
  const title = readString(block.props, "title") ?? "Embedded HTML";
  return (
    <iframe
      // NB: `allow-same-origin` is deliberately absent. Adding it would let the
      // framed document reach back into the host origin and defeat the sandbox.
      sandbox="allow-scripts"
      srcDoc={wrapHtmlDocument(srcdoc)}
      title={title}
      loading="lazy"
      referrerPolicy="no-referrer"
      className="block w-full overflow-hidden rounded-2xl border border-border/70 bg-card"
      style={{ height: 320, colorScheme: "normal" }}
      data-slot="block-html"
      data-block-type="html"
    />
  );
}

/* ============================================================================
 * Tier-1 rich block renderers (docs/rich-blocks-catalog.md)
 *
 * Each unwraps + validates its slice of `props` and delegates to the matching
 * widget, falling back to the safe placeholder on a missing/malformed payload.
 * The four interactive newcomers (suggestions + the backfilled form/approval,
 * and an actioned carousel) emit the shared block-submit event via
 * `useBlockSubmit`, keyed by `block_id`.
 * ========================================================================= */

function CalloutBlock({ block }: { block: Block }) {
  const body = readString(block.props, "body");
  if (body == null) return <UnsupportedBlock block={block} reason="missing body" />;
  const variant = readString(block.props, "variant") as
    | React.ComponentProps<typeof Callout>["variant"]
    | undefined;
  const title = readString(block.props, "title");
  return <Callout variant={variant} title={title} body={body} />;
}

function MediaBlock({ block }: { block: Block }) {
  const kind = readString(block.props, "kind");
  const src = readString(block.props, "src");
  if ((kind !== "image" && kind !== "video" && kind !== "audio") || src == null) {
    return <UnsupportedBlock block={block} reason="missing kind or src" />;
  }
  const widthRaw = readUnknown(block.props, "width");
  const heightRaw = readUnknown(block.props, "height");
  return (
    <Media
      kind={kind as MediaKind}
      src={src}
      alt={readString(block.props, "alt")}
      caption={readString(block.props, "caption")}
      poster={readString(block.props, "poster")}
      width={typeof widthRaw === "number" ? widthRaw : undefined}
      height={typeof heightRaw === "number" ? heightRaw : undefined}
    />
  );
}

function KeyValueBlock({ block }: { block: Block }) {
  const pairs = readArray(block.props, "pairs");
  if (!pairs) return <UnsupportedBlock block={block} reason="missing pairs" />;
  const columnsRaw = readUnknown(block.props, "columns");
  const columns = columnsRaw === 2 ? 2 : columnsRaw === 1 ? 1 : undefined;
  return (
    <KeyValue
      title={readString(block.props, "title")}
      pairs={pairs as KeyValuePair[]}
      columns={columns}
    />
  );
}

function CarouselBlock({ block }: { block: Block }) {
  const items = readArray(block.props, "items");
  const submitTo = readString(block.props, "submit_to");
  const emit = useBlockSubmit({ block_id: block.block_id, type: block.type, submit_to: submitTo });
  if (!items) return <UnsupportedBlock block={block} reason="missing items" />;
  return (
    <Carousel
      title={readString(block.props, "title")}
      description={readString(block.props, "description")}
      items={items as CarouselEntry[]}
      onAction={(itemId, actionId) => emit({ action: actionId, value: itemId })}
    />
  );
}

function ReasoningBlock({ block }: { block: Block }) {
  const steps = readArray(block.props, "steps");
  const text = readString(block.props, "text");
  if (!steps && text == null) {
    return <UnsupportedBlock block={block} reason="missing steps or text" />;
  }
  return (
    <Reasoning
      title={readString(block.props, "title")}
      steps={steps as ReasoningStep[] | undefined}
      text={text}
      defaultOpen={readBoolean(block.props, "defaultOpen")}
    />
  );
}

function StepsBlock({ block }: { block: Block }) {
  const steps = readArray(block.props, "steps");
  if (!steps) return <UnsupportedBlock block={block} reason="missing steps" />;
  const elapsedRaw = readUnknown(block.props, "elapsedMs");
  return (
    <Steps
      title={readString(block.props, "title")}
      steps={steps as StepItem[]}
      elapsedMs={typeof elapsedRaw === "number" ? elapsedRaw : undefined}
      receipt={readUnknown(block.props, "receipt")}
    />
  );
}

function SuggestionsBlock({ block }: { block: Block }) {
  const suggestions = readArray(block.props, "suggestions");
  const submitTo = readString(block.props, "submit_to");
  const emit = useBlockSubmit({ block_id: block.block_id, type: block.type, submit_to: submitTo });
  if (!suggestions) return <UnsupportedBlock block={block} reason="missing suggestions" />;
  return (
    <Suggestions
      prompt={readString(block.props, "prompt")}
      suggestions={suggestions as SuggestionChip[]}
      receipt={readUnknown(block.props, "receipt")}
      onSelect={(chip) => emit({ action: chip.id, value: chip.value ?? chip.label })}
    />
  );
}

function FormBlock({ block }: { block: Block }) {
  const fields = readArray(block.props, "fields");
  const submitTo = readString(block.props, "submit_to");
  const emit = useBlockSubmit({ block_id: block.block_id, type: block.type, submit_to: submitTo });
  if (!fields) return <UnsupportedBlock block={block} reason="missing fields" />;
  return (
    <Form
      title={readString(block.props, "title")}
      description={readString(block.props, "description")}
      fields={fields as FormField[]}
      submitLabel={readString(block.props, "submitLabel")}
      receipt={readUnknown(block.props, "receipt")}
      onSubmit={(values) => emit({ values })}
    />
  );
}

function ApprovalBlock({ block }: { block: Block }) {
  const options = readArray(block.props, "options");
  const submitTo = readString(block.props, "submit_to");
  const emit = useBlockSubmit({ block_id: block.block_id, type: block.type, submit_to: submitTo });
  if (!options) return <UnsupportedBlock block={block} reason="missing options" />;
  return (
    <Approval
      title={readString(block.props, "title")}
      body={readString(block.props, "body")}
      options={options as ApprovalOption[]}
      receipt={readUnknown(block.props, "receipt")}
      onDecide={(option) => emit({ action: option.value, value: option.value })}
    />
  );
}

/* ============================================================================
 * Post-process signal block renderers (ais-agent-runtime#115)
 *
 * The runtime's response post-processor emits a batch of post-answer signals
 * (`PostProcessSignals`); the BE relays them and the FE hosts recover each into
 * a block on the just-finalised message. Each signal maps to one of these
 * blocks. The three interactive ones (`memory_save`, `enriched_suggestion`,
 * `better_answer`) emit the shared block-submit event via `useBlockSubmit`,
 * keyed by `block_id`; `tool_gate_correction` is a display-only inline
 * footnote.
 * ========================================================================= */

function MemorySaveBlock({ block }: { block: Block }) {
  const text = readString(block.props, "text");
  const submitTo = readString(block.props, "submit_to");
  const emit = useBlockSubmit({ block_id: block.block_id, type: block.type, submit_to: submitTo });
  if (text == null) return <UnsupportedBlock block={block} reason="missing text" />;
  return (
    <MemorySave
      text={text}
      prompt={readString(block.props, "prompt")}
      receipt={readUnknown(block.props, "receipt")}
      onSave={(t) => emit({ action: "save", value: t })}
      onDismiss={() => emit({ action: "dismiss" })}
    />
  );
}

/** Narrow one streamed enriched-suggestion entry, dropping anything unlabeled. */
function readEnrichedItem(raw: unknown): EnrichedSuggestionItem | undefined {
  const label = readString(raw, "label");
  if (label == null) return undefined;
  const confidenceRaw = readUnknown(raw, "confidence");
  return {
    label,
    kind: readString(raw, "kind"),
    value: readString(raw, "value"),
    confidence: typeof confidenceRaw === "number" ? confidenceRaw : undefined,
  };
}

function EnrichedSuggestionBlock({ block }: { block: Block }) {
  // The runtime emits these one at a time, but the block accepts either a
  // single suggestion (label/kind/... at the top level) or a `suggestions`
  // list - so the FE can batch a turn's signals into one row.
  const list = readArray(block.props, "suggestions");
  const items = (list ? list.map(readEnrichedItem) : [readEnrichedItem(block.props)]).filter(
    (s): s is EnrichedSuggestionItem => s != null,
  );
  const submitTo = readString(block.props, "submit_to");
  const emit = useBlockSubmit({ block_id: block.block_id, type: block.type, submit_to: submitTo });
  if (items.length === 0) return <UnsupportedBlock block={block} reason="missing label" />;
  return (
    <EnrichedSuggestion
      prompt={readString(block.props, "prompt")}
      suggestions={items}
      receipt={readUnknown(block.props, "receipt")}
      onSelect={(s) => emit({ action: s.kind ?? s.label, value: s.value ?? s.label })}
    />
  );
}

function ToolGateCorrectionBlock({ block }: { block: Block }) {
  const message = readString(block.props, "message");
  if (message == null) return <UnsupportedBlock block={block} reason="missing message" />;
  return <ToolGateCorrection message={message} />;
}

function BetterAnswerBlock({ block }: { block: Block }) {
  const kind = readString(block.props, "kind");
  const label = readString(block.props, "label");
  // Wire field is snake_case `rewritten_query`; accept the camelCase spelling
  // too so either producer shape renders.
  const rewrittenQuery =
    readString(block.props, "rewritten_query") ?? readString(block.props, "rewrittenQuery");
  const submitTo = readString(block.props, "submit_to");
  const emit = useBlockSubmit({ block_id: block.block_id, type: block.type, submit_to: submitTo });
  if (kind == null || label == null || rewrittenQuery == null) {
    return <UnsupportedBlock block={block} reason="missing kind, label, or rewrittenQuery" />;
  }
  return (
    <BetterAnswer
      kind={kind}
      label={label}
      rewrittenQuery={rewrittenQuery}
      receipt={readUnknown(block.props, "receipt")}
      onAccept={(q) => emit({ action: "accept", value: q })}
      onDismiss={() => emit({ action: "dismiss" })}
    />
  );
}

/* ============================================================================
 * Safe placeholder
 * ========================================================================= */

/** A safe, themed fallback for an unknown type, missing props, or a render error. */
function UnsupportedBlock({ block, reason }: { block: Block; reason?: string }) {
  return (
    <div
      role="note"
      className={cn(
        "rounded-2xl border border-dashed border-border bg-muted/30 p-4",
        "text-xs text-muted-foreground",
      )}
      data-slot="block-unsupported"
      data-block-type={String(block.type)}
    >
      <span className="font-medium">Can't display this block</span>{" "}
      <code className="rounded bg-muted px-1 py-0.5 font-mono">{String(block.type)}</code>
      {reason ? <span className="opacity-80"> ({reason})</span> : null}.
    </div>
  );
}

/* ============================================================================
 * The default registry
 * ========================================================================= */

/**
 * Default type -> renderer map. Add a renderer by passing `registry` to
 * `BlockRenderer` rather than editing a switch. `grid` has no built-in renderer
 * yet; it falls through to the safe placeholder until its widget lands.
 *
 * Interactive renderers (`suggestions`, `form`, `approval`, `memory_save`,
 * `enriched_suggestion`, `better_answer`, and an actioned `carousel`) emit the
 * shared block-submit event; wire `onBlockSubmit` on `BlockRenderer` /
 * `BlockList` to receive it.
 */
export const defaultBlockRegistry: BlockRegistry = {
  markdown: MarkdownBlock,
  text: MarkdownBlock,
  chart: ChartBlock,
  table: TableBlock,
  stat: StatBlock,
  citation: CitationBlock,
  code: CodeBlockRenderer,
  artifact: ArtifactBlockRenderer,
  html: HtmlBlock,
  // Tier-1 rich blocks (docs/rich-blocks-catalog.md).
  callout: CalloutBlock,
  media: MediaBlock,
  key_value: KeyValueBlock,
  carousel: CarouselBlock,
  reasoning: ReasoningBlock,
  steps: StepsBlock,
  suggestions: SuggestionsBlock,
  // Interactive blocks backfilled with the receipt settled-state convention.
  form: FormBlock,
  approval: ApprovalBlock,
  // Post-process signal blocks (ais-agent-runtime#115).
  memory_save: MemorySaveBlock,
  enriched_suggestion: EnrichedSuggestionBlock,
  tool_gate_correction: ToolGateCorrectionBlock,
  better_answer: BetterAnswerBlock,
};

/**
 * Shallow-merge a consumer registry over the defaults. Exported so consumers
 * can compose their own merged map once and reuse it (e.g. pass a stable
 * reference to avoid re-renders).
 */
export function mergeRegistry(overrides?: BlockRegistry): BlockRegistry {
  if (!overrides) return defaultBlockRegistry;
  return { ...defaultBlockRegistry, ...overrides };
}

/* ============================================================================
 * BlockRenderer
 * ========================================================================= */

export interface BlockRendererProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The K2 block envelope to render. */
  block: Block;
  /**
   * Extra / override renderers, shallow-merged over the built-ins. Pass a
   * stable reference (module constant or `useMemo`) to avoid re-renders.
   */
  registry?: BlockRegistry;
  /**
   * Host handler called when an INTERACTIVE block (`suggestions`, `form`,
   * `approval`, the post-process signals `memory_save` / `enriched_suggestion`
   * / `better_answer`, or an actioned `carousel`) is acted on. Receives one
   * `BlockSubmitEvent` keyed by `block_id`; the same transport for every
   * interactive block. Flows to descendants via context, so a nested
   * interactive block reaches the same handler without prop-drilling.
   */
  onBlockSubmit?: BlockSubmitHandler;
  /**
   * Resolve a nested `{ ref }` pointer to a `Block` for composition blocks
   * (`carousel` today). Inline child envelopes need no resolver; this exists
   * for hosts that send a large block once and reference it from several
   * containers.
   */
  resolveRef?: BlockRefResolver;
}

/**
 * Internal block-render core: looks the type up in the merged registry, wraps
 * the match in an error boundary, and renders the safe placeholder for an
 * unknown type or a renderer that throws. Sets up NO providers (its callers do)
 * so it can be reused both at the top level and as the `renderBlock` a
 * composition block calls for each child - one code path, no provider nesting.
 */
function ResolvedBlock({ block, registry, className, ...rest }: BlockRendererProps) {
  const resolved = React.useMemo(() => mergeRegistry(registry), [registry]);
  const Component = resolved[block.type];

  const content = Component ? (
    <BlockErrorBoundary block={block}>
      <Component block={block} />
    </BlockErrorBoundary>
  ) : (
    <UnsupportedBlock block={block} />
  );

  return (
    <div className={cn("min-w-0", className)} data-slot="block" {...rest}>
      {content}
    </div>
  );
}

/**
 * Render a single block. Wraps the render core in the submit + nested-block
 * registry contexts so interactive blocks emit through `onBlockSubmit` and
 * composition blocks resolve their children against the same merged registry.
 */
export const BlockRenderer = React.forwardRef<HTMLDivElement, BlockRendererProps>(
  function BlockRenderer({ block, registry, onBlockSubmit, resolveRef, className, ...rest }, ref) {
    const resolved = React.useMemo(() => mergeRegistry(registry), [registry]);
    const renderBlock = React.useCallback(
      (b: Block) => <ResolvedBlock block={b} registry={registry} />,
      [registry],
    );
    const registryCtx = React.useMemo(
      () => ({ registry: resolved, resolveRef, renderBlock }),
      [resolved, resolveRef, renderBlock],
    );

    return (
      <BlockSubmitProvider onBlockSubmit={onBlockSubmit}>
        <BlockRegistryProvider value={registryCtx}>
          <div ref={ref} className={cn("min-w-0", className)} data-slot="block" {...rest}>
            <ResolvedBlock block={block} registry={registry} />
          </div>
        </BlockRegistryProvider>
      </BlockSubmitProvider>
    );
  },
);

BlockRenderer.displayName = "BlockRenderer";

export interface BlockListProps extends Omit<BlockRendererProps, "block"> {
  /** Blocks to render in arrival order. */
  blocks: Block[];
}

/**
 * Render a list of blocks in arrival order, keyed by `block_id` so progressive
 * updates patch in place. Sets up the submit + registry contexts once for the
 * whole run. Renders nothing for an empty list.
 */
export const BlockList = React.forwardRef<HTMLDivElement, BlockListProps>(function BlockList(
  { blocks, registry, onBlockSubmit, resolveRef, className, ...rest },
  ref,
) {
  const resolved = React.useMemo(() => mergeRegistry(registry), [registry]);
  const renderBlock = React.useCallback(
    (b: Block) => <ResolvedBlock block={b} registry={registry} />,
    [registry],
  );
  const registryCtx = React.useMemo(
    () => ({ registry: resolved, resolveRef, renderBlock }),
    [resolved, resolveRef, renderBlock],
  );

  if (!blocks || blocks.length === 0) return null;
  return (
    <BlockSubmitProvider onBlockSubmit={onBlockSubmit}>
      <BlockRegistryProvider value={registryCtx}>
        <div
          ref={ref}
          className={cn("flex flex-col", BLOCK_STACK_GAP, className)}
          data-slot="block-list"
          {...rest}
        >
          {blocks.map((block) => (
            <ResolvedBlock key={block.block_id} block={block} registry={registry} />
          ))}
        </div>
      </BlockRegistryProvider>
    </BlockSubmitProvider>
  );
});

BlockList.displayName = "BlockList";

/* ============================================================================
 * Error boundary
 *
 * A render error in one block must not take down the thread (block-registry.md).
 * Class component because error boundaries are the one React capability with no
 * hook equivalent.
 * ========================================================================= */

class BlockErrorBoundary extends React.Component<
  { block: Block; children: React.ReactNode },
  { failed: boolean }
> {
  constructor(props: { block: Block; children: React.ReactNode }) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  // Reset when the block identity changes, so a transient bad payload that is
  // later corrected (same block_id, new props) can recover instead of staying
  // stuck on the placeholder.
  componentDidUpdate(prev: { block: Block }) {
    if (this.state.failed && prev.block !== this.props.block) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (this.state.failed) {
      return <UnsupportedBlock block={this.props.block} reason="render error" />;
    }
    return this.props.children;
  }
}
